/* eslint-disable max-len */
/* eslint-disable max-lines */
import { useMemo } from 'react';
import {
    Box, Text, useBreakpointValue, useToken,
    VStack,
} from '@chakra-ui/react';
import { animated } from '@react-spring/web';
import {
    ComputedDatum, MayHaveLabel, ResponsivePie, PieSvgProps,
} from '@nivo/pie';
import { FaIcon, GraphLegend, NonIdealState } from '@acp/core-ui';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { PortfolioFacets } from '@acp/shared';
import { useNavigate } from 'react-router-dom';
import { useGetStats } from '../../hooks/use-get-stats';
import { usePropertyManager } from '../../../agency';
import { PortfolioGraphSkeleton } from './graph-skeleton';
import { NewWorkOrderButton } from '../../../property-request';

type Datum = MayHaveLabel & { id: string; color: string; value: number };
type ArcLinkLabelComponentProps = Parameters<NonNullable<PieSvgProps<Datum>['arcLinkLabelComponent']>>[0];

export function CustomArcLinkLabel(props: ArcLinkLabelComponentProps) {
    const {
        label,
        style: {
            textPosition,
            textAnchor,
            textColor,
            path,
            linkColor,
        },
    } = props;

    const lines = label != null ? label.split('\n') : [];

    const AnimatedPath = animated('path');
    const AnimatedText = animated('text');
    const AnimatedTSpan = animated('tspan');

    return (
        <g>
            <AnimatedPath d={path} fill="none" stroke={linkColor} strokeWidth={1} />
            <AnimatedText
                transform={textPosition}
                textAnchor={textAnchor}
                dominantBaseline="central"
                style={{ fill: textColor, fontSize: 12 }}
            >
                {lines.map((line, idx) => (
                    <AnimatedTSpan key={line} x={0} dy={idx === 0 ? 0 : '1.2em'}>
                        {line}
                    </AnimatedTSpan>
                ))}
            </AnimatedText>
        </g>
    );
}

export function PortfolioGraph({ propertyManagerId }: { propertyManagerId?: string | null }) {
    const navigate = useNavigate();
    const compliantPieColor = useToken('colors', 'success.500');
    const bookingPieColor = useToken('colors', 'success.100');
    const complianceIssuePieColor = useToken('colors', 'danger.400');
    const fontSizeSm = useToken('fontSizes', 'sm');
    const fontSizeXs = useToken('fontSizes', 'xs');
    const pieMargin = useBreakpointValue({
        base: {
            top: -60, right: 60, bottom: 30, left: 60,
        },
        sm: {
            top: 80, right: 80, bottom: 100, left: 80,
        },
    });
    const pieBorderWidth = useBreakpointValue({
        base: 1,
        md: 2,
    });
    const enableArcLinkLabels = useBreakpointValue({
        base: false,
        md: true,
    });
    const enableArcLabels = useBreakpointValue({
        base: true,
        md: false,
    });

    const { isLoading, data } = useGetStats(propertyManagerId);
    const {
        propertyManager,
    } = usePropertyManager(propertyManagerId ?? null);

    const pieDatum = useMemo(() => {
        if (isLoading || data === undefined) return [];

        const pieData: (MayHaveLabel & { id: string; color: string; value: number })[] = [];

        // The order is important here, Nivo will render these in the order we give it
        // the data points, we want to render the smallest possible first and last so that the
        // tendrils end up on the left/right side of the top,
        // so there's less chance of them overlapping
        if (data.portfolioStats.BOOKING_IN_PROGRESS > 0) {
            pieData.push({
                id: 'Booking in progress',
                label: 'Booking in\nprogress',
                value: data.portfolioStats.BOOKING_IN_PROGRESS,
                color: bookingPieColor,
            });
        }

        const totalComplianceIssues = data.portfolioStats.COMPLIANCE_ISSUES;
        const totalCompliant = data.portfolioStats.COMPLIANT + data.portfolioStats.PENDING_ITEMS;

        if (data.portfolioStats.COMPLIANT > 0) {
            pieData.push({
                id: 'Compliant',
                label: 'Compliant',
                value: totalCompliant,
                color: compliantPieColor,
            });
        }

        if (totalComplianceIssues > 0) {
            pieData.push({
                id: 'Compliance issues',
                label: 'Compliance issues',
                value: totalComplianceIssues,
                color: complianceIssuePieColor,
            });
        }

        return pieData;
    }, [isLoading, data]);

    if (isLoading || data === undefined) {
        return <PortfolioGraphSkeleton />;
    }

    const {
        COMPLIANT,
        BOOKING_IN_PROGRESS,
        COMPLIANCE_ISSUES,
        PENDING_ITEMS,
    } = data.portfolioStats;
    const totalValue = COMPLIANCE_ISSUES + PENDING_ITEMS + BOOKING_IN_PROGRESS + COMPLIANT;

    const getPercentage = (value: number) => ((value / totalValue) * 100).toFixed(1);

    const handleClickSlice = (datum: ComputedDatum<MayHaveLabel & { id: string; color: string; value: number }>) => {
        const { id } = datum;
        const searchParams = new URLSearchParams();

        if (id === 'Compliant') {
            searchParams.append('facet', PortfolioFacets.CompliantStatus);
            searchParams.append('facet', PortfolioFacets.PendingItemsStatus);
        }

        if (id === 'Compliance issues') {
            searchParams.append('facet', PortfolioFacets.ComplianceIssuesStatus);
        }

        if (id === 'Booking in progress') {
            searchParams.append('facet', PortfolioFacets.BookingInProgressStatus);
        }

        if (propertyManagerId !== undefined && propertyManagerId !== null) {
            searchParams.append('property-manager', propertyManagerId);
        }

        navigate(`/portfolio?${searchParams.toString()}`, {
            replace: true,
        });
    };

    if (pieDatum.length === 0) {
        return (
            <NonIdealState
                iconComponent={<FaIcon icon={faExclamationCircle} size="9x" color="muted.200" />}
                title={`There are no properties on service ${propertyManager?.name !== undefined ? ` for ${propertyManager.name}` : ''}`}
                py={24}
            >
                <Text align="center">
                    Speak to your account manager to enrol properties
                    in your portfolio, or&nbsp;
                    <NewWorkOrderButton
                        variant="link"
                        textDecoration="underline"
                    >
                        click here to enrol a property now
                    </NewWorkOrderButton>
                </Text>
            </NonIdealState>
        );
    }

    return (
        <VStack w="full">
            <Box w="99%" h={96} className="svg-overflow-visible">
                <ResponsivePie
                    data={pieDatum}
                    margin={pieMargin}
                    colors={(d) => d.data.color}
                    theme={{
                        text: {
                            fontFamily: "'Roboto'",
                        },
                        labels: {
                            text: {
                                fontSize: fontSizeSm,
                            },
                        },
                        legends: {
                            text: {
                                fontSize: fontSizeXs,
                            },
                        },
                        tooltip: {
                            container: {
                                fontSize: fontSizeSm,
                            },
                            chip: {
                                borderRadius: '100px',
                            },
                        },
                    }}
                    // startAngle={-160}
                    activeOuterRadiusOffset={8}
                    borderWidth={pieBorderWidth}
                    borderColor="white"
                    valueFormat={(value) => `${new Intl.NumberFormat('en-AU').format(value)} properties`}
                    enableArcLabels={enableArcLabels}
                    enableArcLinkLabels={enableArcLinkLabels}
                    arcLinkLabelComponent={CustomArcLinkLabel}
                    arcLinkLabel={(d) => `${d.label} (${getPercentage(d.value)}%)`}
                    arcLinkLabelsSkipAngle={0}
                    arcLinkLabelsTextColor="#000"
                    arcLinkLabelsThickness={1}
                    arcLinkLabelsStraightLength={12}
                    arcLinkLabelsColor={{ from: 'color' }}
                    arcLabelsSkipAngle={15}
                    arcLabelsTextColor={{
                        from: 'color',
                        modifiers: [
                            [
                                'darker',
                                2,
                            ],
                        ],
                    }}
                    onClick={handleClickSlice}
                />
            </Box>
            <GraphLegend data={pieDatum.map((pieData) => ({
                label: pieData.label,
                legendIconColor: pieData.color,
            }))}
            />
        </VStack>
    );
}
